import { buildSalePriceSuffix } from "@fero/components";
import HideSourceOutlinedIcon from "@mui/icons-material/HideSourceOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import {
  Box,
  Button,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import type { StorageLocationView } from "adl-gen/ferovinum/app/api";
import type {
  Currency,
  PurchaseRequestSalePriceType,
} from "adl-gen/ferovinum/app/db";
import type {
  PurchaseRequestClosedLineItem,
  PurchaseRequestLineItem,
} from "adl-gen/ferovinum/app/nompurchaser";
import { CurrencyRenderer } from "components/widgets/currency-renderer/currency-renderer";
import { format } from "date-fns";
import { useState } from "react";
import { getVesselCapacity } from "utils/model-utils";
import { ColGroup } from "../../colgroup";
import { OrganisationProductSummary } from "../../organisation-product-summary/organisation-product-summary";

export interface OrganisationPurchaseRequestSummaryTableProps {
  purchaserCurrency: Currency;
  settlementCurrency: Currency;
  products: PurchaseRequestLineItem[] | PurchaseRequestClosedLineItem[];
  isUnfulfilledEndState?: boolean;
  storageLocation: StorageLocationView;
  salePriceType: PurchaseRequestSalePriceType;
  isPreview?: boolean;
}

export const OrganisationPurchaseRequestSummaryTable = ({
  isUnfulfilledEndState = false,
  products,
  purchaserCurrency,
  settlementCurrency,
  storageLocation,
  salePriceType,
  isPreview = false,
}: OrganisationPurchaseRequestSummaryTableProps) => {
  const [showAttributeSelection, setShowAttributeSelection] = useState(false);
  const hasFreeUnit = products.some((product) => product.freeUnits != null);
  const hasDiscount = products.some((product) => product.priceDiscount != null);
  const hasAttributeSelection = products.some(
    (product) => product.attributeSelection.length !== 0,
  );

  // If the warehouse is not in GB, the sale price suffix should be based on the sale price type otherwise will always be Ex Duty, Ex VAT
  const suffix =
    storageLocation.country !== "GB"
      ? buildSalePriceSuffix(salePriceType)
      : "Ex Duty, Ex VAT";
  return (
    <Stack spacing={2} width="100%">
      {hasAttributeSelection && !isPreview && (
        <Button
          variant="outlined"
          startIcon={
            showAttributeSelection ? (
              <HideSourceOutlinedIcon />
            ) : (
              <VisibilityOutlinedIcon />
            )
          }
          sx={{ alignSelf: "flex-end", borderRadius: "99px" }}
          onClick={() => setShowAttributeSelection(!showAttributeSelection)}
        >
          {showAttributeSelection
            ? "Hide Batch Information"
            : "Show Batch Information"}
        </Button>
      )}
      <Table sx={{ borderTopLeftRadius: 8, borderTopRightRadius: 8 }}>
        <ColGroup
          widths={[
            ...[50, 20, 20, 20],
            ...(!isUnfulfilledEndState ? [20] : []),
            ...(hasDiscount ? [20] : []),
          ]}
        />
        <TableHead>
          <TableRow>
            <TableCell sx={{ borderTopLeftRadius: 8 }}>Product</TableCell>
            <SummaryTableCell>Quantity</SummaryTableCell>
            {!isUnfulfilledEndState && (
              <SummaryTableCell>
                Average Net Payable
                <br />
                Per Unit {hasFreeUnit ? " (Incl Free)" : ""}
              </SummaryTableCell>
            )}
            <SummaryTableCell>
              Sale Price Per{hasFreeUnit ? " Paid " : " "}Unit
              <br />({suffix})
            </SummaryTableCell>
            {hasDiscount && (
              <SummaryTableCell>Price Discount Included</SummaryTableCell>
            )}
            <SummaryTableCell
              textAlign="right"
              paddingRight
              sx={{ borderTopRightRadius: 8 }}
            >
              Subtotal
            </SummaryTableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {products.map((productData, idx) => (
            <PurchaseRequestSummaryTableRow
              key={productData.product.id + idx}
              productData={productData}
              isUnfulfilledEndState={isUnfulfilledEndState}
              purchaserCurrency={purchaserCurrency}
              settlementCurrency={settlementCurrency}
              hasDiscount={hasDiscount}
              showAttributeSelection={showAttributeSelection}
            />
          ))}
        </TableBody>
      </Table>
    </Stack>
  );
};

interface PurchaseRequestSummaryTableRowProps {
  productData: PurchaseRequestLineItem | PurchaseRequestClosedLineItem;
  isUnfulfilledEndState: boolean;
  purchaserCurrency: Currency;
  settlementCurrency: Currency;
  hasDiscount: boolean;
  showAttributeSelection: boolean;
}

const PurchaseRequestSummaryTableRow = ({
  productData,
  isUnfulfilledEndState,
  purchaserCurrency,
  settlementCurrency,
  hasDiscount,
  showAttributeSelection,
}: PurchaseRequestSummaryTableRowProps) => {
  const {
    product,
    paidUnits,
    cleanPricePerPaidUnit,
    priceDiscount,
    freeUnits,
    attributeSelection,
  } = productData;
  const averageNetPayablePerUnit =
    "averageNetPayablePerUnit" in productData
      ? productData.averageNetPayablePerUnit
      : undefined;

  return (
    <>
      <TableRow>
        <TableCell>
          <OrganisationProductSummary
            {...product.value}
            productId={product.id}
            vesselCapacity={getVesselCapacity(product.value.vesselSize)}
          />
        </TableCell>
        <SummaryTableCell>
          <Typography>{paidUnits.value}</Typography>
          {freeUnits && (
            <Typography variant="caption">+{freeUnits.value} free</Typography>
          )}
        </SummaryTableCell>
        {!isUnfulfilledEndState && (
          <SummaryTableCell>
            <CurrencyRenderer
              maximumFractionDigits={4}
              currency={settlementCurrency}
              value={averageNetPayablePerUnit}
            />
          </SummaryTableCell>
        )}
        <SummaryTableCell>
          <CurrencyRenderer
            maximumFractionDigits={4}
            currency={purchaserCurrency}
            value={cleanPricePerPaidUnit}
          />
        </SummaryTableCell>
        {hasDiscount && (
          <SummaryTableCell>
            {priceDiscount ? (
              <CurrencyRenderer
                value={priceDiscount}
                currency={purchaserCurrency}
              />
            ) : (
              "-"
            )}
          </SummaryTableCell>
        )}
        <SummaryTableCell textAlign="right" paddingRight>
          <CurrencyRenderer
            maximumFractionDigits={4}
            currency={purchaserCurrency}
            value={
              productData.paidUnits.value *
              Number(productData.cleanPricePerPaidUnit)
            }
          />
        </SummaryTableCell>
      </TableRow>
      {showAttributeSelection &&
        attributeSelection.map((attribute) => (
          <TableRow key={attribute.batchNumber}>
            <SummaryTableCell textAlign={"right"}>
              <Typography>
                <Box component="span" fontWeight="bold">
                  {attribute.batchNumber}
                </Box>
                {attribute.bestBeforeDate
                  ? ` (${format(new Date(attribute.bestBeforeDate), "dd MMM yyyy")})`
                  : " N/A"}
              </Typography>
            </SummaryTableCell>
            <SummaryTableCell>
              <Typography>{attribute.quantity}</Typography>
            </SummaryTableCell>
            <SummaryTableCell />
            {!isUnfulfilledEndState && <SummaryTableCell />}
            <SummaryTableCell />
            {hasDiscount && <SummaryTableCell />}
            <SummaryTableCell />
          </TableRow>
        ))}
    </>
  );
};

export const SummaryTableCell = styled(TableCell, {
  shouldForwardProp: (prop) => prop !== "paddingRight" && prop !== "textAlign",
})<{
  paddingRight?: boolean;
  textAlign?: "left" | "center" | "right";
}>(({ theme, paddingRight, textAlign }) => ({
  textAlign: textAlign ?? "center",
  paddingRight: paddingRight ? theme.spacing(4) : 0,
}));
